import React, { forwardRef } from 'react';
import type { ElementType } from 'react';
import { StyledFlex } from './HBFlex.styles';
import {
    buildBaseCss,
    buildSpacingCss,
    buildSizingCss,
    buildChildCss,
    pickBaseProps,
    pickSpacingProps,
    pickSizingProps,
    pickChildProps,
    responsiveSpacingCss,
    responsiveValueCss,
    isResponsive,
    withDisplayName
} from '../../styles/layout/layout.utils';
import type { HBFlexProps, FlexDirection } from './HBFlex.types';
import type {
    StrictPolymorphicProps,
    PolymorphicRef
} from '../../styles/layout/layout.types';

type HBFlexPolymorphicProps<C extends ElementType = 'div'> =
    StrictPolymorphicProps<C, HBFlexProps>;

type HBFlexComponent = <C extends ElementType = 'div'>(
    props: HBFlexPolymorphicProps<C> & { ref?: PolymorphicRef<C> }
) => React.ReactElement | null;

/**
 * Extracts the base (initial) direction for default align calculation.
 */
function getBaseDirection(direction: HBFlexProps['direction']): FlexDirection {
    if (direction === undefined) {
        return 'row';
    }
    if (isResponsive(direction)) {
        return direction.initial || 'row';
    }
    return direction;
}

function getDefaultAlign(direction: FlexDirection): string {
    return direction === 'column' || direction === 'column-reverse'
        ? 'stretch'
        : 'center';
}

function buildFlexCss(props: HBFlexProps, baseDirection: FlexDirection) {
    const {
        display,
        direction,
        align,
        justify,
        wrap,
        alignContent,
        gap,
        gapX,
        gapY
    } = props;

    // Display: always output via $layoutCss (static or responsive)
    const displayCss = responsiveValueCss(display ?? 'flex', ['display']);

    // Direction: always output via $layoutCss (static or responsive)
    const directionCss = responsiveValueCss(direction ?? 'row', [
        'flex-direction'
    ]);

    // Align: use explicit value, or fall back to direction-dependent default
    const alignCss = responsiveValueCss(
        align ?? getDefaultAlign(baseDirection),
        ['align-items']
    );

    return [
        ...buildBaseCss(props),
        ...buildSpacingCss(props),
        ...buildSizingCss(props),
        ...buildChildCss(props),
        displayCss,
        directionCss,
        alignCss,
        justify ? responsiveValueCss(justify, ['justify-content']) : '',
        wrap ? responsiveValueCss(wrap, ['flex-wrap']) : '',
        alignContent ? responsiveValueCss(alignContent, ['align-content']) : '',
        // gap is the shorthand; gapX/gapY are axis-specific overrides.
        // When gap is set, gapX/gapY are ignored to avoid cascade ambiguity.
        responsiveSpacingCss(gap, ['gap']),
        ...(gap
            ? []
            : [
                  responsiveSpacingCss(gapX, ['column-gap']),
                  responsiveSpacingCss(gapY, ['row-gap'])
              ])
    ];
}

/**
 * Flexbox layout container with direction-dependent alignment defaults.
 * Row direction defaults to `align-items: center`, column to `stretch`.
 * Supports gap, justify, wrap, and responsive breakpoint objects for all props.
 */
const HBFlex = forwardRef(
    <C extends ElementType = 'div'>(
        {
            as,
            children,
            display,
            direction = 'row',
            align,
            justify,
            wrap,
            alignContent,
            gap,
            gapX,
            gapY,
            displayName,
            ...allProps
        }: HBFlexPolymorphicProps<C>,
        ref: PolymorphicRef<C>
    ) => {
        const [baseProps, afterBase] = pickBaseProps(allProps);
        const [spacingProps, afterSpacing] = pickSpacingProps(afterBase);
        const [sizingProps, afterSizing] = pickSizingProps(afterSpacing);
        const [childProps, afterChild] = pickChildProps(afterSizing);
        // Defense in depth: `className` and `style` are omitted from
        // HBFlexProps, but styled-components still injects a runtime
        // `className` prop when this component is wrapped via
        // `styled(HBFlex)`, and a consumer could still pass `style` via
        // `as any`/JS, regardless of the declared type. Strip both explicitly
        // so neither has any visual effect.
        const {
            className: _discardedClassName,
            style: _discardedStyle,
            ...rest
        } = afterChild as typeof afterChild & {
            className?: string;
            style?: React.CSSProperties;
        };
        const baseDirection = getBaseDirection(direction);
        const layoutCss = buildFlexCss(
            {
                display,
                direction,
                align,
                justify,
                wrap,
                alignContent,
                gap,
                gapX,
                gapY,
                ...baseProps,
                ...spacingProps,
                ...sizingProps,
                ...childProps
            },
            baseDirection
        );

        return withDisplayName(
            displayName,
            <StyledFlex
                as={as || 'div'}
                ref={ref}
                $layoutCss={layoutCss}
                data-component-name={displayName}
                {...rest}
            >
                {children}
            </StyledFlex>
        );
    }
) as HBFlexComponent;

(HBFlex as React.FC).displayName = 'HBFlex';

export default HBFlex;
